import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Image as ImageIcon,
  Upload,
  RefreshCw,
  X,
  History,
  Download,
  Plus,
  Settings,
  Edit
} from 'lucide-react';
import axios from 'axios';
import AddBrand from './AddBrand';

// API配置
const API_BASE_URL = '/api';

// 类型定义
interface Brand {
  id: number;
  name: string;
  color: string;
  logo: string;
  description?: string;
  brand_colors?: string;
  brand_style?: string;
  target_audience?: string;
  slogan?: string;
  logo_position?: string;
  logo_size?: string;
  color_adjustment?: string;
}

interface EventInfo {
  name: string;
  type: string;
  description: string;
  theme: string;
  color_scheme: string;
  keywords: string;
  suggestions: string;
}

interface HistoryItem {
  id: number;
  url: string;
  date: string;
  brand: string;
  type: string;
  event: string;
  prompt?: string;
}

// 模拟农历转换（仅用于展示效果）
const getLunarDate = (date: Date) => {
  const day = date.getDate();
  const map = ['初一','初二','初三','初四','初五','初六','初七','初八','初九','初十',
               '十一','十二','十三','十四','十五','十六','十七','十八','十九','二十',
               '廿一','廿二','廿三','廿四','廿五','廿六','廿七','廿八','廿九','三十'];
  return `农历 ${map[day % 30] || '初一'}`;
};

const WEEKDAYS = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];

export default function App() {
  // --- 状态管理 ---
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAddBrand, setShowAddBrand] = useState(false);
  const [showEditBrand, setShowEditBrand] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [showImg2ImgModal, setShowImg2ImgModal] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [eventInfo, setEventInfo] = useState<EventInfo | null>(null);
  const [expandedPromptId, setExpandedPromptId] = useState<number | null>(null);
  const [isLoadingEvent, setIsLoadingEvent] = useState(false);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState<string>('');

  // 图生图相关状态
  const [refImages, setRefImages] = useState<File[]>([]);

  // 品牌hover状态（用于Safari兼容性）
  const [hoveredBrandId, setHoveredBrandId] = useState<number | null>(null);

  // 日期选择器引用
  const dateInputRef = useRef<HTMLInputElement>(null);

  // 检测浏览器是否支持 showPicker API
  const [supportsShowPicker, setSupportsShowPicker] = useState(false);

  useEffect(() => {
    // 检测是否支持 showPicker API
    const testInput = document.createElement('input');
    testInput.type = 'date';
    setSupportsShowPicker('showPicker' in testInput);
  }, []);

  const loadBrands = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/brands`);
      if (response.data.success) {
        const apiBrands = response.data.brands.map((b: any) => ({
          id: b.id,
          name: b.name,
          color: b.brand_colors ? JSON.parse(b.brand_colors)[0]?.code || '#000000' : '#000000',
          logo: b.name[0],
          description: b.description,
          brand_colors: b.brand_colors,
          brand_style: b.brand_style,
          target_audience: b.target_audience,
          slogan: b.slogan,
          logo_position: b.logo_position,
          logo_size: b.logo_size,
          color_adjustment: b.color_adjustment
        }));
        setBrands(apiBrands);
        if (apiBrands.length > 0) {
          setSelectedBrand(apiBrands[0]);
          // 从数据库加载该品牌的历史记录
          loadBrandHistory(apiBrands[0].id, apiBrands[0].name).then(brandHistory => {
            if (brandHistory.length > 0) {
              setGeneratedImage(brandHistory[0].url);
            }
          });
        }
      }
    } catch (error) {
      console.error('加载品牌失败:', error);
    }
  };


  const loadBrandHistory = async (brandId: number, brandName?: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/posters/${brandId}`);
      if (response.data.success) {
        const historyItems = response.data.posters.map((poster: any) => ({
          id: poster.id,
          url: poster.image_path,
          date: poster.created_at.split(' ')[0],
          brand: brandName || selectedBrand?.name || '',
          type: '文生图',
          event: '历史海报',
          prompt: poster.prompt || ''
        }));
        saveHistory(historyItems);
        return historyItems;
      }
      return [];
    } catch (error) {
      console.error('加载品牌历史失败:', error);
      return [];
    }
  };

  const loadEventInfo = useCallback(async (date: Date) => {
    setIsLoadingEvent(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/date-event`, {
        date: formatDateKey(date)
      });
      if (response.data.success && response.data.event) {
        setEventInfo(response.data.event);
      }
    } catch (error) {
      console.error('加载事件信息失败:', error);
    } finally {
      setIsLoadingEvent(false);
    }
  }, []);

  // --- 加载品牌数据 ---
  useEffect(() => {
    loadBrands();
  }, []);

  // --- 加载事件信息 ---
  useEffect(() => {
    if (currentDate) {
      loadEventInfo(currentDate);
    }
  }, [currentDate, loadEventInfo]);

  // --- 辅助函数 ---
  
  const formatDateKey = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const getHolidayName = (date: Date) => {
    if (eventInfo) {
      return eventInfo.name;
    }
    return '平平无奇的一天';
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentDate(new Date(e.target.value));
  };

  const handleDateClick = () => {
    if (supportsShowPicker && dateInputRef.current) {
      // Chrome等支持 showPicker 的浏览器
      dateInputRef.current.showPicker();
    } else if (dateInputRef.current) {
      // Safari等不支持 showPicker 的浏览器，直接点击input
      dateInputRef.current.click();
    }
  };

  const handleBrandChange = async (brand: Brand) => {
    setSelectedBrand(brand);
    // 从数据库加载该品牌的历史记录
    const brandHistory = await loadBrandHistory(brand.id, brand.name);
    saveHistory(brandHistory);
    // 如果该品牌有历史记录，显示最新的海报
    if (brandHistory.length > 0) {
      setGeneratedImage(brandHistory[0].url);
    } else {
      setGeneratedImage(null);
    }
  };

  const handleEditBrand = (brand: Brand) => {
    setEditingBrand(brand);
    setShowEditBrand(true);
  };

  const saveHistory = (newHistory: HistoryItem[]) => {
    setHistory(newHistory);
  };

  const handleGenerate = async () => {
    if (!selectedBrand) {
      alert('请先选择一个品牌');
      return;
    }

    setIsGenerating(true);

    // 立即关闭图生图modal，让用户知道已经开始生成
    const wasImg2Img = refImages.length > 0;
    if (wasImg2Img) {
      setShowImg2ImgModal(false);
    }

    try {
      if (wasImg2Img) {
        // 使用图生图API
        const formData = new FormData();
        refImages.forEach((image, index) => {
          formData.append(`ref_image_${index}`, image);
        });
        formData.append('brand_id', selectedBrand.id.toString());
        formData.append('event_name', eventInfo?.name || '');
        formData.append('event_type', eventInfo?.type || '普通');

        // 等待所有图片添加完成
        await new Promise(resolve => setTimeout(resolve, 500));

        const response = await axios.post(`${API_BASE_URL}/generate-with-character`, formData);

        if (response.data.success) {
          const imageUrl = response.data.image_path;
          setGeneratedImage(imageUrl);
          saveHistory([{
            id: Date.now(),
            url: imageUrl,
            date: formatDateKey(currentDate),
            brand: selectedBrand.name,
            type: '图生图',
            event: eventInfo?.name || '自定义日期',
            prompt: response.data.prompt || ''
          }, ...history]);
        } else {
          alert('生成失败: ' + (response.data.error || '未知错误'));
        }
      } else {
        // 使用文生图API
        const response = await axios.post(`${API_BASE_URL}/generate-by-date`, {
          brand_id: selectedBrand.id,
          date: formatDateKey(currentDate),
          event_name: eventInfo?.name || '',
          event_type: eventInfo?.type || '普通'
        });

        if (response.data.success) {
          const imageUrl = response.data.image_path;
          setGeneratedImage(imageUrl);
          saveHistory([{
            id: Date.now(),
            url: imageUrl,
            date: formatDateKey(currentDate),
            brand: selectedBrand.name,
            type: '文生图',
            event: eventInfo?.name || '自定义日期',
            prompt: response.data.prompt || ''
          }, ...history]);
        } else {
          alert('生成失败: ' + (response.data.error || '未知错误'));
        }
      }
    } catch (error) {
      console.error('生成失败:', error);
      alert('生成失败: ' + (error as Error).message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = async (historyItem: HistoryItem) => {
    if (!selectedBrand) {
      alert('请先选择一个品牌');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/generate-by-date`, {
        brand_id: selectedBrand.id,
        date: historyItem.date,
        event_name: historyItem.event || '',
        event_type: '普通'
      });

      if (response.data.success) {
        const imageUrl = response.data.image_path;
        setGeneratedImage(imageUrl);
        saveHistory([{
          id: Date.now(),
          url: imageUrl,
          date: historyItem.date,
          brand: selectedBrand.name,
          type: '文生图',
          event: historyItem.event || '重新生成',
          prompt: response.data.prompt
        }, ...history]);
      } else {
        alert('生成失败: ' + (response.data.error || '未知错误'));
      }
    } catch (error) {
      console.error('重新生成失败:', error);
      alert('重新生成失败: ' + (error as Error).message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async (historyItem: HistoryItem) => {
    try {
      // 获取图片的完整URL
      const imageUrl = historyItem.url.startsWith('http') 
        ? historyItem.url 
        : `${window.location.origin}${historyItem.url}`;
      
      // 创建一个临时的a标签来下载
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `poster_${historyItem.brand}_${historyItem.date}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('下载失败:', error);
      alert('下载失败: ' + (error as Error).message);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    setRefImages(prev => [...prev, ...files]);
  };

  // --- 组件渲染 ---

  return (
    <div className="app-container">
      
      {/* 纹理背景层 */}
      <div className="texture-background" 
           style={{backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`}}>
      </div>

      {/* 顶部导航 (极简) */}
      <nav className="nav">
        <div className="nav-title">
          <span className="nav-badge">品牌海报</span>
        </div>
      </nav>

      <main className="main-content">
        
        {/* --- 左侧：核心日历生成器 --- */}
        <div className="calendar-container">
          
          {/* 挂环效果 */}
          <div className="calendar-rings">
             <div className="calendar-ring"></div>
             <div className="calendar-ring"></div>
          </div>

          {/* 日历主体 */}
          <div className="calendar-body">
            
            {/* 1. 日历头部：日期与节日 */}
            <div className="calendar-header" onClick={handleDateClick}>
               {/* 隐藏的日期选择器 */}
               <input
                 type="date"
                 ref={dateInputRef}
                 className={`calendar-date-input ${supportsShowPicker ? 'supports-showpicker' : 'fallback-mode'}`}
                 onChange={handleDateChange}
                 value={formatDateKey(currentDate)}
               />

               <div className="calendar-year-month">
                 <span>{currentDate.getFullYear()}年</span>
                 <span>{currentDate.getMonth() + 1}月</span>
               </div>
               
               {/* 巨大日期数字 */}
               <div className="calendar-day-number">
                 {currentDate.getDate()}
               </div>
               
               <div className="calendar-weekday">
                 <span className="calendar-weekday-text">{WEEKDAYS[currentDate.getDay()]}</span>
                 <span className="calendar-weekday-text">{getLunarDate(currentDate)}</span>
               </div>

               {/* 节日徽章 */}
               <div className={`calendar-event-badge ${isLoadingEvent ? 'loading' : ''}`}>
                 {isLoadingEvent ? '今日节日查询中...' : getHolidayName(currentDate)}
               </div>

               <div className="calendar-hint">
                 点击此处切换日期 👆
               </div>
            </div>

            {/* 2. 日历中部：图片生成区 */}
            <div className="calendar-content">
              {/* 纸张纹理叠加 */}
              <div className="paper-texture"></div>

              {isGenerating ? (
                <div className="generating-state">
                  <div className="generating-spinner"></div>
                  <p className="generating-text">AI 正在绘制海报...</p>
                  <p className="generating-subtext">正在分析品牌元素</p>
                </div>
              ) : generatedImage && selectedBrand ? (
                <>
                   <img src={generatedImage} alt="Generated" className="calendar-image" />
                   {/* 模拟Logo合成 */}
                   <div className="calendar-logo"
                        style={{backgroundColor: selectedBrand.color}}>
                     {selectedBrand.logo}
                   </div>
                </>
              ) : (
                <div className="default-state">
                  <div className="default-state-text">宜</div>
                  <div className="default-state-title">生成今日营销海报</div>
                  <div className="default-state-divider"></div>
                  <div className="default-state-text">忌</div>
                  <div className="default-state-title">错失热点流量</div>
                </div>
              )}
            </div>

            {/* 3. 日历底部：操作按钮 */}
            <div className="calendar-footer">
              <button 
                onClick={handleGenerate}
                disabled={isGenerating}
                className="generate-button"
              >
                 <RefreshCw size={24} className={`icon ${isGenerating ? 'spinning' : ''}`} />
                 {isGenerating ? '生成中...' : '立即生成今日海报'}
              </button>

              <button 
                onClick={() => setShowImg2ImgModal(true)}
                className="img2img-button"
              >
                <ImageIcon size={16} />
                <span>我要参考图片生成 (图生图)</span>
              </button>
            </div>
            
            {/* 撕页效果阴影 */}
            <div className="calendar-tear"></div>
          </div>
        </div>

        {/* --- 右侧：品牌选择列表 --- */}
        <div className="brand-list">
           {brands.map(brand => (
             <div
                key={brand.id}
                className="brand-item"
                onMouseEnter={() => setHoveredBrandId(brand.id)}
                onMouseLeave={() => setHoveredBrandId(null)}
             >
                {/* Tooltip (Desktop) */}
                <div className="brand-tooltip">
                  {brand.name}
                </div>

                <button
                                  onClick={() => handleBrandChange(brand)}
                                  className={`brand-button ${selectedBrand?.id === brand.id ? 'selected' : ''}`}
                                  style={{backgroundColor: brand.color, color: 'white'}}
                                >
                                  {brand.logo}
                                  {selectedBrand?.id === brand.id && (
                                    <div className="brand-pulse"></div>
                                  )}
                                </button>

                {/* 编辑按钮 - 仅在选中品牌时hover显示 */}
                {selectedBrand?.id === brand.id && (hoveredBrandId === brand.id) && (
                  <button
                    className="brand-edit-button visible"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditBrand(brand);
                    }}
                    title="编辑品牌"
                  >
                    <Edit size={14} />
                  </button>
                )}

                {/* 选中指示器 */}
                {selectedBrand?.id === brand.id && (
                  <div className="brand-indicator"></div>
                )}
             </div>
           ))}
           
           <button
             className="add-brand-button"
             onClick={() => setShowAddBrand(true)}
           >
             <span className="text-2xl font-light">+</span>
           </button>
        </div>

      </main>

      {/* --- 底部：历史生成记录 --- */}
      <section className="history-section">
        <div className="history-header">
          <History size={20} />
          <h2 className="history-title">往期海报印记</h2>
        </div>
        
        {history.length === 0 ? (
          <div className="history-empty">
             暂无历史生成记录，快去生成第一张吧
          </div>
        ) : (
          <div className="history-grid">
            {history.map((item) => (
              <div key={item.id} className="history-item">
                <div className="history-image-container">
                  <img src={item.url} alt="History" className="history-image" />
                </div>
                <div className="history-info">
                  <span>{item.date}</span>
                  <span className={`history-type-badge ${(item as any).type === '文生图' ? 'text-to-image' : 'image-to-image'}`}>
                    {(item as any).type}
                  </span>
                  {item.prompt && expandedPromptId === item.id && (
                    <div className="history-prompt">
                      <div className="history-prompt-text">{item.prompt}</div>
                      <button
                        className="history-prompt-copy"
                        onClick={(e) => {
                          e.stopPropagation();
                          try {
                            navigator.clipboard.writeText(item.prompt || '').then(() => {
                            }).catch((err) => {
                              console.error('复制失败:', err);
                              const textArea = document.createElement('textarea');
                              textArea.value = item.prompt || '';
                              textArea.style.position = 'fixed';
                              textArea.style.left = '-999999px';
                              document.body.appendChild(textArea);
                              textArea.select();
                              try {
                                document.execCommand('copy');
                              } catch (err) {
                                console.error('降级复制也失败:', err);
                              }
                              document.body.removeChild(textArea);
                            });
                          } catch (err) {
                            console.error('复制提示词出错:', err);
                          }
                        }}
                        title="复制提示词"
                      >
                        <Settings size={14}/>
                      </button>
                    </div>
                  )}
                </div>
                {/* Hover Actions */}
                <div className="history-actions">
                  {item.prompt && (
                    <button
                      className="history-action-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentPrompt(item.prompt || '');
                        setShowPromptModal(true);
                      }}
                      title="查看提示词"
                    >
                      <Settings size={16}/>
                    </button>
                  )}
                  <button
                    className="history-action-button"
                    onClick={() => handleRegenerate(item)}
                    title="重新生成"
                  >
                    <RefreshCw size={16}/>
                  </button>
                  <button 
                    className="history-action-button"
                    onClick={() => handleDownload(item)}
                    title="下载"
                  >
                    <Download size={16}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* --- 弹窗：图生图面板 --- */}
      {showImg2ImgModal && (
        <div className="modal-overlay">
          <div className="modal-content">

            {/* 弹窗头部 */}
            <div className="modal-header">
               <div>
                 <h3 className="modal-title">上传参考图片</h3>
                 <p className="modal-subtitle">AI 将参考图片中的人物/构图进行创作</p>
               </div>
               <button onClick={() => setShowImg2ImgModal(false)} className="modal-close-button">
                 <X size={20} className="text-gray-500" />
               </button>
            </div>

            {/* 弹窗内容 */}
            <div className="modal-body">
               <div className="upload-area">
                  <input type="file" multiple accept="image/*" onChange={handleFileUpload} className="upload-input" />
                  <div className="upload-icon">
                    <Upload size={32} />
                  </div>
                  <p className="upload-title">点击上传或拖拽图片至此</p>
                  <p className="upload-subtitle">支持 JPG, PNG (最大 5MB)</p>
               </div>

               {/* 已上传图片预览 */}
               {refImages.length > 0 && (
                 <div className="uploaded-images">
                   <h4 className="uploaded-images-title">已选参考图 ({refImages.length})</h4>
                   <div className="uploaded-images-grid">
                     {refImages.map((img, idx) => (
                       <div key={idx} className="uploaded-image-item">
                         <img src={URL.createObjectURL(img)} alt="Reference" className="uploaded-image" />
                         <button
                           onClick={() => setRefImages(prev => prev.filter((_, i) => i !== idx))}
                           className="uploaded-image-remove"
                         >
                           <X size={12} />
                         </button>
                       </div>
                     ))}
                   </div>
                 </div>
               )}
            </div>

            {/* 弹窗底部 */}
            <div className="modal-footer">
              <button
                onClick={() => setShowImg2ImgModal(false)}
                className="modal-button modal-button-secondary"
              >
                取消
              </button>
              <button
                onClick={handleGenerate}
                disabled={refImages.length === 0}
                className="modal-button modal-button-primary"
              >
                <span>确认并生成</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- 弹窗：提示词显示面板 --- */}
      {showPromptModal && (
        <div className="modal-overlay">
          <div className="modal-content prompt-modal-content">

            {/* 弹窗头部 */}
            <div className="modal-header">
               <div>
                 <h3 className="modal-title">提示词详情</h3>
                 <p className="modal-subtitle">AI 生成此海报时使用的提示词</p>
               </div>
               <button onClick={() => setShowPromptModal(false)} className="modal-close-button">
                 <X size={20} className="text-gray-500" />
               </button>
            </div>

            {/* 弹窗内容 */}
            <div className="modal-body">
               <div className="prompt-display">
                 <pre className="prompt-text">{currentPrompt}</pre>
               </div>
            </div>

            {/* 弹窗底部 */}
            <div className="modal-footer">
              <button
                onClick={() => {
                  try {
                    navigator.clipboard.writeText(currentPrompt).then(() => {
                      alert('提示词已复制到剪贴板');
                    }).catch((err) => {
                      console.error('复制失败:', err);
                      const textArea = document.createElement('textarea');
                      textArea.value = currentPrompt;
                      textArea.style.position = 'fixed';
                      textArea.style.left = '-999999px';
                      document.body.appendChild(textArea);
                      textArea.select();
                      try {
                        document.execCommand('copy');
                        alert('提示词已复制到剪贴板');
                      } catch (err) {
                        console.error('降级复制也失败:', err);
                        alert('复制失败，请手动复制');
                      }
                      document.body.removeChild(textArea);
                    });
                  } catch (err) {
                    console.error('复制提示词出错:', err);
                    alert('复制失败，请手动复制');
                  }
                }}
                className="modal-button modal-button-primary"
              >
                复制提示词
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 添加品牌模态框 */}
      {showAddBrand && (
        <div className="modal-overlay">
          <AddBrand
            onBrandAdded={() => {
              setShowAddBrand(false);
              loadBrands();
            }}
            onCancel={() => setShowAddBrand(false)}
          />
        </div>
      )}

      {/* 编辑品牌模态框 */}
      {showEditBrand && editingBrand && (
        <div className="modal-overlay">
          <AddBrand
            brand={editingBrand}
            onBrandAdded={() => {
              setShowEditBrand(false);
              setEditingBrand(null);
              loadBrands();
            }}
            onCancel={() => {
              setShowEditBrand(false);
              setEditingBrand(null);
            }}
          />
        </div>
      )}

    </div>
  );
}